/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useLocation } from "react-router-dom";
import { Request_Error } from "../../assets/images";
import { CountDownTimer, Header, CustomMessageModal } from "../../components";
import useApp from "../../hooks/useApp";
import {
  ROUTES,
  NEXT_AUTH_FACTOR,
  ACCOUNT_STATE,
  EVENT_NAME,
} from "../../constants";
import api from "../../services";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import { logAnalyticEvent } from "../../analytics";
import { convertTimestamp } from "../../utils";
import "./styles.scss";

const VerifyOTP = () => {
  const navigate = useNavigateWithQuery();
  const { t } = useTranslation();
  const { isRTL, tenantConfig } = useApp();
  const location = useLocation();
  const { phoneNumber, isNewCustomer, isForgotPassword, isChangePassword } =
    location.state || {};

  const [code, setCode] = useState("");
  const [showCountdownTimer, setShowCountdownTimer] = useState(true);
  const [accountState, setAccountState] = useState(ACCOUNT_STATE.CREATE);
  const [messageModalData, setMessageModalData] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [startTime, setStartTime] = useState(0);

  const handleNavigation = (nextAuthFactor) => {
    switch (nextAuthFactor) {
      case NEXT_AUTH_FACTOR.VERIFY_PIN:
        navigate(`/${ROUTES.VERIFY_PIN}`, {
          state: {
            ...location.state,
            accountState,
          },
        });
        break;
      case NEXT_AUTH_FACTOR.SET_PIN:
        navigate(`/${ROUTES.VERIFY_PIN}`, {
          state: {
            ...location.state,
            accountState,
          },
        });
        break;
      default:
        break;
    }
  };

  const handleVerifyOTP = async () => {
    try {
      const payload = { username: phoneNumber, otp: code };

      let response;

      if (isNewCustomer) {
        response = await api.registerVerifyOTP(payload);
      } else if (isForgotPassword) {
        response = await api.forgotPasswordVerifyOTP(payload);
      } else if (isChangePassword) {
        response = await api.changePasswordVerifyOTP(payload);
      } else {
        response = await api.loginVerifyOTP(payload);
      }
      handleNavigation(response.next_auth_factor);
    } catch (error) {
      setErrorMessage(t("verificationCode.error_message"));
      console.error("Error in handleVerifyOTP method: ", error);
    }
  };

  const handleInitiate = async () => {
    try {
      let payload;
      if (isNewCustomer) {
      } else if (isForgotPassword) {
        payload = { username: phoneNumber };
        await api.forgotPasswordInitiate(payload);
      } else if (isChangePassword) {
        payload = { username: phoneNumber };
        await api.changePasswordInitiate(payload);
      } else {
      }
    } catch (error) {
      setMessageModalData({
        heading: "Oops! Something Went Wrong",
        description: `${error?.response?.data?.message || error?.message}`,
        msgImage: Request_Error,
      });
      console.error("Error in handleInitiate method: ", error);
    }
  };

  const handlePinState = () => {
    if (isNewCustomer) {
      setAccountState(ACCOUNT_STATE.CREATE);
    } else if (isForgotPassword) {
      setAccountState(ACCOUNT_STATE.FORGOT);
    } else if (isChangePassword) {
      setAccountState(ACCOUNT_STATE.CHANGE_PASSWORD);
    } else {
      setAccountState(ACCOUNT_STATE.LOGIN);
    }
  };

  const getInputType = (type) => {
    switch (type) {
      case "numeric":
        return "number";
      default:
        return "text";
    }
  };

  const onChangeCode = (code) => {
    setCode(code);
    setErrorMessage("");
  };

  const handleResendOtp = () => {
    setShowCountdownTimer(true);
    logAnalyticEvent(EVENT_NAME.RESEND_OTP, {});
  };

  useEffect(() => {
    handleInitiate();
    handlePinState();
  }, [location.state]);

  useEffect(() => {
    if (code.length === tenantConfig?.otp_setting?.content?.length) {
      const inputs = document.querySelectorAll("input");
      inputs.forEach((input) => input.blur());
      handleVerifyOTP();
    }
  }, [code, tenantConfig?.otp_setting?.content?.length]);

  useEffect(() => {
    setStartTime(Date.now());
    return () => {
      const endTime = Date.now();
      logAnalyticEvent(EVENT_NAME.PAGE_VIEW, {
        page_title: ROUTES.VERIFY_OTP,
        path: `${window.location.origin}/${ROUTES.VERIFY_OTP}`,
        time_spent: convertTimestamp(endTime - startTime),
      });
    };
  }, []);

  return (
    <div className="verify-otp-container">
      <Header backEnable onClick={() => navigate(-1)} />
      <div className="content-container">
        <h1 className="title">{t("verificationCode.heading")}</h1>
        <p className="description">
          {t("verificationCode.description", {
            otpCount: tenantConfig?.otp_setting?.content.length,
          })}
        </p>
        <p dir="ltr" className="phone-number-text">
          {phoneNumber}
        </p>
        <OtpInput
          value={code}
          onChange={onChangeCode}
          numInputs={tenantConfig?.otp_setting?.content.length}
          containerStyle={`otp-input-container ${
            isRTL ? "flex-row-reverse" : ""
          }`}
          inputStyle={"otp-input"}
          inputType={getInputType(
            tenantConfig?.otp_setting.content.character_set
          )}
          renderInput={(props) => <input {...props} />}
          shouldAutoFocus
        />
        {errorMessage ? <p className="error-text">{errorMessage}</p> : null}
      </div>
      <div className="footer-container">
        <div className="resend-container">
          <span className={`resend-text ${isRTL ? "resend-text-rtl" : ""}`}>
            {showCountdownTimer
              ? t("verificationCode.footer_text2")
              : t("verificationCode.footer_text1")}
          </span>
          {showCountdownTimer ? (
            <CountDownTimer
              initialSeconds={
                tenantConfig?.otp_setting.content.retry_policy
                  .retry_delay_in_seconds
              }
              onCountdownEnd={() => setShowCountdownTimer(false)}
            />
          ) : (
            <span className="resend-link" onClick={() => handleResendOtp()}>
              {t("verificationCode.resend")}
            </span>
          )}
        </div>
      </div>
      <CustomMessageModal
        show={!!messageModalData}
        onClickClose={() => setMessageModalData(null)}
        image={messageModalData?.msgImage}
        heading={messageModalData?.heading}
        description={messageModalData?.description}
      />
    </div>
  );
};

export default VerifyOTP;
